/* eslint-disable camelcase */
import {
  createSelector,
} from 'reselect';
import {
  DateTime,
} from 'luxon';
import * as R from 'ramda';

import {
  getResourceMap,
  getResourceById,
  getResourceLists,
  getResourceMappedList,
} from './resources';

import {
  getProfile,
} from './profile';

import {
  userColors,
} from '../utils';

import {
  sortByDate,
  sortByPosition,
  sortByOrderName,
} from './utils';

import type {
  ResourceType,
} from '../types';

const eventMapper = (fileds: any) => (item: any) => ({
  ...fileds,
  ...item,
  title: item.workout_name,
  start: new Date(item.begins_at),
  end: new Date(item.ends_at || item.begins_at),
  startTime: DateTime.fromISO(item.begins_at).toFormat('t'),
  color: userColors[item.person_id % userColors.length],
});

export const getEventsForBigCalendar = (userId: string) => createSelector(
  [
    getResourceMappedList('workoutEvents', userId || 'all'),
    getResourceMappedList('activityLogs', userId || 'all'),
  ],
  (events, logs) => [
    ...events.map(eventMapper({ isLog: false })),
    ...logs.map(eventMapper({ isLog: true })),
  ],
);

export const getCurrentEvent = (resourceType: ResourceType, id: number) => createSelector(
  [
    getResourceById(resourceType, id),
    getResourceMap('clients'),
    getProfile,
  ],
  (event, users, me) => {
    if (!event) return {};

    return {
      ...users[event.person_id]?.user_profile,
      ...(me.id === event.person_id ? me.user_profile : {}),
      ...event,
      start: DateTime.fromISO(event.begins_at).toFormat('LL/dd/yyyy hh:mm a'),
    };
  },
);

const groupByBegin = R.pipe(
  R.groupBy(
    item => DateTime.fromISO(item.begins_at).toISODate(),
  ),
  R.map(array => array.reverse()),
);

export const getEventsGroupByBeginsAt = (userId?: string) => createSelector(
  getResourceMappedList('workoutEvents', userId || 'all'),
  events => groupByBegin(events.sort(sortByDate('begins_at'))),
);

export const getUpcomingWorkouts = () => createSelector(
  getResourceMappedList('workoutEvents'),
  events => events
    .filter(
      event => !event.is_completed && DateTime.fromISO((event.ends_at)) > DateTime.local(),
    )
    .sort(sortByDate('begins_at', true)),
);

export const getMissedWorkouts = (userId?: string) => createSelector(
  getResourceMappedList('workoutEvents', (userId || 'all')),
  events => events
    .filter(
      event => !event.is_completed && DateTime.fromISO(event.ends_at) < DateTime.local(),
    )
    .sort(sortByDate('begins_at', true)),
);

export const getPastWorkouts = userId => createSelector(
  getResourceMappedList('workoutEvents', (userId || 'all')),
  events => events
    .filter(
      event => DateTime.fromISO(event.ends_at) <= DateTime.local(),
    )
    .sort(sortByDate('begins_at', true)),
);

export const getExerciseResults = exerciseId => createSelector(
  getResourceMappedList(
    'exerciseResults',
    `workout_exercise_${exerciseId}`,
  ),
  results => results.sort(
    sortByDate('updated_at', true),
  ).reduce((
    acc,
    {
      exercise_result_items: items,
      ...rest
    },
  ) => [
    ...acc,
    {
      ...rest,
      items: items
        ? items.sort(sortByPosition)
        : [],
    },
  ],
  []),
);

export const getEventExercises = (eventId: number) => createSelector(
  getResourceMappedList('exercises', `event_${eventId}`),
  getResourceLists('exerciseResults'),
  (exercises, resultIds) => {
    const list = R.pipe(
      R.groupBy(
        item => item.order_name,
      ),

      R.values,
      R.reduce((accumulator, items) => {
        let item = items[0];
        let alternatives = [];

        if (items.length > 1) {
          item = items.find(e => {
            const id = `workout_exercise_${e.workout_exercise_id}`;
            return resultIds[id].length > 0;
          }) || item;
          alternatives = items.filter(i => i.id !== item.id);
        }

        item.alternatives = alternatives;

        return [
          ...accumulator,
          item,
        ];
      }, []),
      R.sort(sortByOrderName),
    )(exercises);

    return list;
  },
);

export const getAllEventExercises = (eventId: number) => createSelector(
  getResourceMappedList('exercises', `event_${eventId}`),
  (all) => {
    const {
      warmups,
      exercises,
      cooldowns,
    } = all.reduce((acc, item) => ({
      warmups: item.is_warmup
        ? [...acc.warmups, item]
        : acc.warmups,
      cooldowns: item.is_cooldown
        ? [...acc.cooldowns, item]
        : acc.cooldowns,
      exercises: (!item.is_cooldown && !item.is_warmup)
        ? [...acc.exercises, item]
        : acc.exercises,
    }), {
      warmups: [],
      exercises: [],
      cooldowns: [],
    });

    return [
      ...warmups.sort(sortByPosition),
      ...exercises.sort(sortByPosition),
      ...cooldowns.sort(sortByPosition),
    ];
  },
);

export const getExerciseProperties = (exerciseId: number) => createSelector(
  getResourceMappedList(
    'exerciseProperties',
    `workout_exercise_${exerciseId}`,
  ),
  getResourceMappedList(
    'exerciseResults',
    `workout_exercise_${exerciseId}`,
  ),
  (properties, results) => {
    const items = results.reduce((acc, r) => [...acc, ...r.exercise_result_items], []);
    return properties
      .map((p: any) => ({
        ...p,
        result: items.find((r: any) => r.exercise_property_id === p.id),
      }))
      .sort(sortByPosition);
  },
);
